import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  billingAccessApi,
  billingPeriodsApi,
  employeeAgreementsApi,
  signatureTypesApi,
} from "../api/employeeAgreements";
import type {
  CreateAgreementInput,
  CreateSignatureTypeInput,
  ReplaceSignatureTypeInput,
} from "../types";

const KEYS = {
  agreements: (params?: object) => ["employee-agreements", params] as const,
  agreement: (id: string) => ["employee-agreement", id] as const,
  signatories: ["employee-agreement-signatories"] as const,
  signatureTypes: (agreement: string, includeDeleted?: boolean) =>
    ["employee-signature-types", agreement, includeDeleted] as const,
  billingPeriods: (params?: object) => ["employee-billing-periods", params] as const,
  billingPeriod: (id: string) => ["employee-billing-period", id] as const,
  billingAccess: (params?: object) =>
    ["employee-signature-billing-access", params] as const,
};

// ── Agreements ──────────────────────────────────────────────────────────────

export function useEmployeeAgreements(params?: {
  employee?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: KEYS.agreements(params),
    queryFn: () => employeeAgreementsApi.list(params),
  });
}

export function useEmployeeAgreement(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.agreement(id ?? ""),
    queryFn: () => employeeAgreementsApi.detail(id as string),
    enabled: !!id,
  });
}

export function useCreateAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAgreementInput) =>
      employeeAgreementsApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-agreements"] });
      qc.invalidateQueries({ queryKey: KEYS.signatories });
    },
  });
}

export function useUpdateAgreement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<CreateAgreementInput>) =>
      employeeAgreementsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.agreement(id) });
      qc.invalidateQueries({ queryKey: ["employee-agreements"] });
    },
  });
}

export function useTerminateAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeAgreementsApi.terminate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-agreements"] });
      qc.invalidateQueries({ queryKey: ["employee-agreement"] });
      qc.invalidateQueries({ queryKey: KEYS.signatories });
    },
  });
}

export function useSignatories() {
  return useQuery({
    queryKey: KEYS.signatories,
    queryFn: () => employeeAgreementsApi.signatories(),
  });
}

// ── Signature types ─────────────────────────────────────────────────────────

export function useSignatureTypes(agreement: string, includeDeleted = false) {
  return useQuery({
    queryKey: KEYS.signatureTypes(agreement, includeDeleted),
    queryFn: () =>
      signatureTypesApi.list({ agreement, include_deleted: includeDeleted }),
    enabled: !!agreement,
  });
}

export function useCreateSignatureType(agreementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSignatureTypeInput) =>
      signatureTypesApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-signature-types"] });
      qc.invalidateQueries({ queryKey: KEYS.agreement(agreementId) });
    },
  });
}

export function useUpdateSignatureType(agreementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateSignatureTypeInput> }) =>
      signatureTypesApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-signature-types"] });
      qc.invalidateQueries({ queryKey: KEYS.agreement(agreementId) });
    },
  });
}

export function useSoftDeleteSignatureType(agreementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signatureTypesApi.softDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-signature-types"] });
      qc.invalidateQueries({ queryKey: KEYS.agreement(agreementId) });
    },
  });
}

export function useSoftDeleteAndReplaceSignatureType(agreementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ReplaceSignatureTypeInput }) =>
      signatureTypesApi.softDeleteAndReplace(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-signature-types"] });
      qc.invalidateQueries({ queryKey: KEYS.agreement(agreementId) });
    },
  });
}

// ── Billing periods ─────────────────────────────────────────────────────────

export function useBillingPeriods(params?: { employee?: string }) {
  return useQuery({
    queryKey: KEYS.billingPeriods(params),
    queryFn: () => billingPeriodsApi.list(params),
  });
}

export function useBillingPeriod(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.billingPeriod(id ?? ""),
    queryFn: () => billingPeriodsApi.detail(id as string),
    enabled: !!id,
  });
}

export function useGeneratePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { agreement_id: string; year: number; month: number }) =>
      billingPeriodsApi.generate(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-billing-periods"] });
    },
  });
}

export function useUploadInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) =>
      billingPeriodsApi.uploadInvoice(id, fileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-billing-periods"] });
      qc.invalidateQueries({ queryKey: ["employee-billing-period"] });
    },
  });
}

export function useMarkPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingPeriodsApi.markPaid(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-billing-periods"] });
      qc.invalidateQueries({ queryKey: ["employee-billing-period"] });
    },
  });
}

// ── Billing access grants (CEO only) ────────────────────────────────────────

export function useBillingAccessGrants(params?: {
  director?: string;
  employee?: string;
}) {
  return useQuery({
    queryKey: KEYS.billingAccess(params),
    queryFn: () => billingAccessApi.list(params),
  });
}

export function useCreateBillingAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ director, employee }: { director: string; employee: string }) =>
      billingAccessApi.create(director, employee),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-signature-billing-access"] });
    },
  });
}

export function useDeleteBillingAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingAccessApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-signature-billing-access"] });
    },
  });
}
